import { message, Row, Typography } from "antd";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useInfiniteScroll from "react-infinite-scroll-hook";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ICollections } from "../../../helpers/types";
import useFetchUserSettings from "../../../hooks/userHooks/useFetchUserSettings";
import {
  clearCollectionsCache,
  deleteCollectionById,
  getOtherUserCollections,
  getPublicCollections,
  getUserCollections,
  updateCollectionsLocally,
} from "../../../redux/collections/collectionsSlice";
import { AppDispatch, StoreState } from "../../../redux/store";
import { updateTutorialVisibilityStatus } from "../../../redux/users/usersSlice";
import { collectionService } from "../../../services/collection.service";
import SpinnerComponent from "../../SpinnerComponent/SpinnerComponent";
import CollectionItemComponent from "../CollectionItemComponent";
import CollectionDeleteModalComponent from "../Modals/CollectionDeleteModalComponent";
import CollectionEditModalComponent from "../Modals/CollectionEditModalComponent";
import CollectionHideModalComponent from "../Modals/CollectionHideModalComponent";
import CollectionTrainingModalComponent from "../Modals/CollectionTrainingModalComponent";
import "./index.less";

type CollectionGridProps = {
  isMyProfile?: boolean;
  creatorId?: string;
  username?: string;
  isPublic?: boolean;
  showModal?: () => void;
  goToPublicationTab?: () => void;
  setCollectionEditMode?: (value: boolean) => void;
  profileName?: string;
};

const CollectionGridComponent: React.FC<CollectionGridProps> = ({
  isMyProfile = false,
  username,
  creatorId,
  isPublic = false,
  showModal,
  goToPublicationTab,
  setCollectionEditMode,
  profileName,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { userCollections, otherUserCollections, publicCollections, shouldNavigateToPublicationPage } = useSelector(
    (state: StoreState) => state.collections
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [loading, setLoading] = useState<"idle" | "pending" | "succeeded" | "failed">("pending");
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [longPressTimer, setLongPressTimer] = useState<NodeJS.Timeout | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [editCollectionModalOpen, setEditCollectionModalOpen] = useState<boolean>(false);
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [openHideModal, setOpenHideModal] = useState<boolean>(false);
  const [collectionId, setCollectionId] = useState<string>("");
  const [isHidden, setIsHidden] = useState<boolean>(false);
  const searchParams = new URLSearchParams(window.location.search);
  const collectionTab = searchParams.get("ctc");
  const { currentUserSettings } = useFetchUserSettings(isPublic);

  // Derive collections data from Redux state
  const cachedData = useMemo(() => {
    if (isPublic) {
      return publicCollections;
    }
    if (isMyProfile) {
      return userCollections;
    }
    return otherUserCollections.find((u) => u.id === creatorId) || null;
  }, [isPublic, isMyProfile, creatorId, publicCollections, userCollections, otherUserCollections]);

  const collections: ICollections[] = cachedData?.collections || [];

  // Sync hasMore from cached data whenever it changes
  useEffect(() => {
    if (cachedData) {
      setHasMore(cachedData.hasMore);
    }
  }, [cachedData]);

  // Reset pagination when creator changes
  useEffect(() => {
    setCurrentPage(1);
    setHasMore(true);
  }, [isMyProfile, creatorId]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsEditMode(false);
        setCollectionEditMode && setCollectionEditMode(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Fetch collections from API
  useEffect(() => {
    if (!creatorId && !isMyProfile) return;
    if (creatorId === "") return;

    // If we already have cached data and this is the initial load, skip fetching
    if (cachedData && currentPage === 1) {
      setLoading("succeeded");
      return;
    }

    if (!hasMore) return;

    setLoading("pending");
    let action;
    if (isPublic) {
      action = dispatch(getPublicCollections({ creatorId: creatorId!, page: currentPage }));
    } else if (isMyProfile) {
      action = dispatch(getUserCollections(currentPage));
    } else {
      action = dispatch(getOtherUserCollections({ creatorId: creatorId!, page: currentPage }));
    }
    action
      .then(() => setLoading("succeeded"))
      .catch(() => {
        setLoading("failed");
        setHasMore(false);
      });
  }, [isMyProfile, creatorId, isPublic, currentPage, dispatch]);

  // Navigate to publication tab if no collections found
  useEffect(() => {
    if (shouldNavigateToPublicationPage) {
      goToPublicationTab && goToPublicationTab();
    }
  }, [shouldNavigateToPublicationPage, goToPublicationTab]);

  const handleNavigation = (collectionId: string) => {
    if (isEditMode) {
      return null;
    }

    if (isPublic) {
      if (showModal) showModal();
    } else {
      if (isMyProfile) {
        navigate("/creator/collection/" + collectionId + "/" + username);
      } else {
        if (collectionTab) {
          navigate("/common/collection/" + collectionId + "/" + username + "?ctc=collections", { replace: true });
        } else {
          navigate("/common/collection/" + collectionId + "/" + username, { replace: true });
        }
      }
    }
  };

  const loadMoreCollections = () => {
    if (hasMore && loading !== "pending") {
      setCurrentPage((prevPage) => prevPage + 1);
    }
  };

  const [sentryRef] = useInfiniteScroll({
    loading: loading === "pending",
    hasNextPage: hasMore,
    onLoadMore: loadMoreCollections,
  });

  const handleLongPressStart = () => {
    if (isMyProfile) {
      const timer = setTimeout(() => {
        setIsEditMode(true);
        setCollectionEditMode && setCollectionEditMode(true);
      }, 1000);
      setLongPressTimer(timer);
    }
  };

  const handleLongPressEnd = () => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
  };

  const handleEditCollection = (collection: ICollections) => {
    setEditCollectionModalOpen(true);
    setCollectionId(collection._id);
    setIsHidden(collection.isHidden);
  };

  const openModal = (value: string) => {
    switch (value) {
      case "delete":
        setEditCollectionModalOpen(false);
        setOpenDeleteModal(true);
        break;
      case "hide":
        setEditCollectionModalOpen(false);
        setOpenHideModal(true);
        break;
      default:
        break;
    }
  };

  const closeModal = (value: string) => {
    switch (value) {
      case "edit":
        setEditCollectionModalOpen(false);
        break;
      case "delete":
        setOpenDeleteModal(false);
        break;
      case "hide":
        setOpenHideModal(false);
        break;
      default:
        break;
    }
  };

  const deleteCollection = async () => {
    if (collectionId) {
      try {
        const resultAction = await dispatch(deleteCollectionById(collectionId));
        if (deleteCollectionById.fulfilled.match(resultAction)) {
          message.success(t("collection-successfully-deleted"));
          setOpenDeleteModal(false);
        } else {
          console.error("Error deleting collection:", resultAction.payload);
        }
      } catch (error) {
        console.error("Error deleting collection:", error);
      }
    }
  };

  const hideShowCollection = async () => {
    if (collectionId) {
      try {
        collectionService.hideShowCollection(collectionId).then(() => {
          message.success(t("collection-successfully-modified"));
          setOpenHideModal(false);
          dispatch(clearCollectionsCache({ creatorId, isMyProfile }));
        });
      } catch (error) {
        console.error("Error when hiding the collection:", error);
      }
    }
  };

  const handleDragStart = (event: React.DragEvent, index: number) => {
    event.dataTransfer.setData("index", String(index));
  };

  const handleDrop = (event: React.DragEvent, index: number) => {
    const draggedIndex = Number(event.dataTransfer.getData("index"));
    const updatedCollections = [...collections];

    const draggedItem = updatedCollections[draggedIndex];
    const targetItem = updatedCollections[index];

    updatedCollections.splice(draggedIndex, 1);
    updatedCollections.splice(index, 0, draggedItem);
    dispatch(updateCollectionsLocally({ creatorId, isMyProfile, collections: updatedCollections }));

    sendReorderedDataToBackend(draggedItem._id, draggedItem.order, targetItem.order);

    event.preventDefault();
  };

  const sendReorderedDataToBackend = async (draggedCollectionId: string, draggedOrder: number, targetOrder: number) => {
    if (draggedOrder === targetOrder) return;
    try {
      await collectionService.updateCollectionOrder(draggedCollectionId, draggedOrder, targetOrder);
      message.success(t("successful-modification"));
    } catch (error) {
      console.error("Error updating collection order:", error);
      message.error(t("something-want-wrong"));
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
  };

  const handleTutorialRead = async () => {
    dispatch(updateTutorialVisibilityStatus("hasSeenTrainingCollectionModal"));
  };

  return (
    <div ref={containerRef}>
      <Row className="custom-spacing" gutter={4}>
        {collections.length === 0 && loading === "succeeded" && (
          <Row className="w-full justify-content-center mt-50">
            <Typography.Text className="font-16-bold text-white-color">{t("no-collection-at-the-moment")}</Typography.Text>
          </Row>
        )}
        {collections.map((collection, index) => (
          <CollectionItemComponent
            key={collection._id}
            index={index}
            collection={collection}
            isEditMode={isEditMode}
            isMyProfile={isMyProfile}
            showTrainingOverlay={index === 0 && isMyProfile && !currentUserSettings?.visibilityStatus?.hasSeenTrainingCollectionModal}
            onEdit={handleEditCollection}
            onNavigate={handleNavigation}
            onLongPressStart={handleLongPressStart}
            onLongPressEnd={handleLongPressEnd}
            draggable={isEditMode}
            onDragStart={(e: React.DragEvent) => handleDragStart(e, index)}
            onDrop={(e: React.DragEvent) => handleDrop(e, index)}
            onDragOver={handleDragOver}
            profileName={profileName}
            username={username}
            collectionHiddenPressed={() => {
              setIsEditMode(true);
              setCollectionEditMode && setCollectionEditMode(true);
            }}
          />
        ))}
        {(loading === "pending" || hasMore) && (
          <Row className="w-full justify-content-center mt-20">
            <SpinnerComponent refProp={sentryRef} />
          </Row>
        )}
      </Row>
      <CollectionEditModalComponent
        isOpen={editCollectionModalOpen}
        isHidden={isHidden}
        onClose={() => closeModal("edit")}
        openDeleteModal={() => openModal("delete")}
        openHideModal={() => openModal("hide")}
        navigateToDetails={() => {
          navigate("/creator/collection/" + collectionId + "/" + username);
        }}
      />
      <CollectionDeleteModalComponent isOpen={openDeleteModal} onClose={() => closeModal("delete")} deleteCollection={deleteCollection} />
      <CollectionHideModalComponent
        isOpen={openHideModal}
        hide={isHidden}
        onClose={() => closeModal("hide")}
        hideShowCollection={hideShowCollection}
      />
      {collections.length !== 0 && isMyProfile && !currentUserSettings?.visibilityStatus?.hasSeenTrainingCollectionModal && (
        <CollectionTrainingModalComponent onClose={handleTutorialRead} />
      )}
    </div>
  );
};

export default CollectionGridComponent;
